import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO_URL } from "../mock";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("#home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy: highlight the nav link whose section is most in view
  useEffect(() => {
    const targets = NAV_LINKS
      .map((l) => document.querySelector(l.href))
      .filter(Boolean);
    if (!targets.length) return;

    // Map from element id -> href
    const idToHref = {};
    NAV_LINKS.forEach((l) => {
      const el = document.querySelector(l.href);
      if (el) idToHref[el.id] = l.href;
    });

    const visibility = new Map();
    let atTop = window.scrollY < 100;

    const decide = () => {
      if (atTop) {
        setActive("#home");
        return;
      }
      let bestId = null;
      let bestRatio = 0;
      visibility.forEach((ratio, id) => {
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestId = id;
        }
      });
      if (bestId && idToHref[bestId] && bestRatio > 0) {
        setActive(idToHref[bestId]);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibility.set(entry.target.id, entry.intersectionRatio);
        });
        decide();
      },
      {
        rootMargin: "-40% 0px -50% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    targets.forEach((t) => observer.observe(t));

    const onScrollTop = () => {
      const newAtTop = window.scrollY < 100;
      if (newAtTop !== atTop) {
        atTop = newAtTop;
        decide();
      }
    };
    window.addEventListener("scroll", onScrollTop, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScrollTop);
    };
  }, []);

  const handleNav = (href) => {
    setActive(href);
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md shadow-[0_4px_20px_rgba(27,94,58,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => handleNav("#home")}
          className="flex items-center gap-2 hover:opacity-90 transition-opacity"
        >
          <img
            src={LOGO_URL}
            alt="Tatva Wellness Center"
            className="h-[106px] md:h-[114px] w-auto object-contain"
          />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className={`relative text-[15px] font-medium tracking-wide transition-colors ${
                active === link.href
                  ? "text-[#1b5e3a]"
                  : "text-[#2c342d] hover:text-[#1b5e3a]"
              }`}
            >
              {link.label}
              {active === link.href && (
                <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#1b5e3a] rounded-full" />
              )}
            </button>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden lg:block">
          <button
            onClick={() => handleNav("#book")}
            className="bg-[#1b5e3a] hover:bg-[#164d30] text-white text-sm font-semibold tracking-[0.12em] uppercase px-6 py-3 rounded-md shadow-[0_4px_14px_rgba(27,94,58,0.35)] hover:shadow-[0_6px_20px_rgba(27,94,58,0.45)] transition-all duration-300 hover:-translate-y-0.5"
          >
            Book a Consultation
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden text-[#1b5e3a] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-[#e6ded0]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className="text-left text-[#2c342d] font-medium py-1.5 hover:text-[#1b5e3a] transition-colors"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => handleNav("#book")}
              className="mt-2 bg-[#1b5e3a] hover:bg-[#164d30] text-white text-sm font-semibold tracking-[0.12em] uppercase px-6 py-3 rounded-md w-full"
            >
              Book a Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
